import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader2,
  Plus,
  Search,
  Filter,
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Eye,
  ChevronRight,
  RefreshCw,
  LogIn,
  Download,
  FileSpreadsheet,
  Info
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { apiGet, formatErrorMessage, ApiError } from '../../utils/catalogApi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const STATUS_COLORS = {
  uploaded: 'bg-gray-100 text-gray-700',
  parsing: 'bg-blue-100 text-blue-700',
  validated: 'bg-green-100 text-green-700',
  review_required: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-teal-100 text-teal-700',
  publishing: 'bg-purple-100 text-purple-700',
  published: 'bg-green-200 text-green-800',
  failed: 'bg-red-100 text-red-700',
  canceled: 'bg-gray-200 text-gray-600'
};

const StatusBadge = ({ status }) => (
  <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'}`}>
    {status?.replace('_', ' ')}
  </span>
);

const AdminCatalogImports = ({ onViewImport, onCreateImport }) => {
  const [imports, setImports] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthError, setIsAuthError] = useState(false);
  const [showTemplateInfo, setShowTemplateInfo] = useState(false);
  const [templateFields, setTemplateFields] = useState(null);
  
  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Get auth token from localStorage
  const getAuthToken = () => {
    return localStorage.getItem('auth_token') || '';
  };

  // Download template handler
  const handleDownloadTemplate = async (format, withSample) => {
    try {
      const token = getAuthToken();
      const url = `${API_URL}/api/catalog-imports/admin/templates/download?format=${format}&with_sample=${withSample}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download template');
      }
      
      const blob = await response.blob();
      const filename = `product_import_template${withSample ? '_with_samples' : ''}.${format}`;
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download template. Please try again.');
    }
  };

  // Fetch template fields info
  const fetchTemplateFields = async () => {
    try {
      const data = await apiGet('/catalog-imports/admin/templates/fields');
      setTemplateFields(data);
    } catch (err) {
      console.error('Failed to fetch template fields:', err);
    }
  };

  const fetchImports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setIsAuthError(false);
      
      const data = await apiGet('/catalog-imports/admin/imports', {
        q: search,
        status: statusFilter,
        vendor_id: vendorFilter,
        page: page.toString(),
        page_size: '20'
      });
      
      setImports(data.imports || []);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthorized) {
        setIsAuthError(true);
        setError('Session expired. Please log in again.');
      } else {
        setError(formatErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, vendorFilter, page]);

  const fetchVendors = async () => {
    try {
      const data = await apiGet('/catalog-imports/admin/vendors', { is_active: 'true' });
      setVendors(data.vendors || []);
    } catch (err) {
      console.error('Failed to fetch vendors:', err);
    }
  };

  useEffect(() => {
    fetchImports();
  }, [fetchImports]);

  useEffect(() => {
    fetchVendors();
    fetchTemplateFields();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  if (loading && imports.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="catalog-imports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-heading font-semibold text-ivory-800">Catalog Imports</h2>
          <p className="text-sm text-ivory-500 font-body">Manage multi-vendor product imports</p>
        </div>
        <Button onClick={onCreateImport} data-testid="create-import-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Import
        </Button>
      </div>

      {/* Template Download Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileSpreadsheet className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-blue-900">Import Template</h3>
              <p className="text-sm text-blue-700">Download a template to prepare your product data</p>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            {/* CSV Downloads */}
            <div className="flex items-center gap-1 bg-white rounded-lg border border-blue-200 p-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownloadTemplate('csv', false)}
                className="text-xs h-7 px-2"
                data-testid="download-csv-empty"
              >
                <Download className="w-3 h-3 mr-1" />
                CSV (Empty)
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownloadTemplate('csv', true)}
                className="text-xs h-7 px-2"
                data-testid="download-csv-sample"
              >
                <Download className="w-3 h-3 mr-1" />
                CSV (Sample)
              </Button>
            </div>
            
            {/* Excel Downloads */}
            <div className="flex items-center gap-1 bg-white rounded-lg border border-green-200 p-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownloadTemplate('xlsx', false)}
                className="text-xs h-7 px-2 text-green-700 hover:text-green-800 hover:bg-green-50"
                data-testid="download-xlsx-empty"
              >
                <Download className="w-3 h-3 mr-1" />
                Excel (Empty)
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownloadTemplate('xlsx', true)}
                className="text-xs h-7 px-2 text-green-700 hover:text-green-800 hover:bg-green-50"
                data-testid="download-xlsx-sample"
              >
                <Download className="w-3 h-3 mr-1" />
                Excel (Sample)
              </Button>
            </div>
            
            {/* Info Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowTemplateInfo(!showTemplateInfo)}
              className="h-7 px-2"
              data-testid="template-info-btn"
            >
              <Info className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Template Fields Info */}
        {showTemplateInfo && templateFields && (
          <div className="mt-4 pt-4 border-t border-blue-200">
            <h4 className="font-medium text-blue-900 mb-2">Template Fields ({templateFields.total_fields} total)</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Required Fields */}
              <div className="bg-white rounded-lg p-3 border border-red-100">
                <h5 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Required Fields
                </h5>
                <ul className="space-y-1">
                  {templateFields.required_fields?.map(field => (
                    <li key={field.name} className="text-xs">
                      <span className="font-mono bg-red-50 text-red-700 px-1 py-0.5 rounded">{field.name}</span>
                      <span className="text-gray-600 ml-2">{field.description}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Optional Fields */}
              <div className="bg-white rounded-lg p-3 border border-gray-100">
                <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-gray-400" />
                  Optional Fields
                </h5>
                <ul className="space-y-1 max-h-40 overflow-y-auto">
                  {templateFields.optional_fields?.map(field => (
                    <li key={field.name} className="text-xs">
                      <span className="font-mono bg-gray-50 text-gray-700 px-1 py-0.5 rounded">{field.name}</span>
                      <span className="text-gray-500 ml-2">{field.description}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <p className="text-xs text-blue-600 mt-3">
              Tip: Excel template includes an "Instructions" sheet and "Field Reference" with examples.
            </p>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-ivory-200 p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ivory-400" />
              <Input
                placeholder="Search by filename or ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="import-search"
              />
            </div>
          </div>
          
          <select
            value={vendorFilter}
            onChange={(e) => setVendorFilter(e.target.value)}
            className="px-3 py-2 border border-ivory-300 rounded-lg text-sm"
            data-testid="vendor-filter"
          >
            <option value="">All Vendors</option>
            {vendors.map(v => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-ivory-300 rounded-lg text-sm"
            data-testid="status-filter"
          >
            <option value="">All Statuses</option>
            <option value="uploaded">Uploaded</option>
            <option value="parsing">Parsing</option>
            <option value="validated">Validated</option>
            <option value="review_required">Review Required</option>
            <option value="approved">Approved</option>
            <option value="publishing">Publishing</option>
            <option value="published">Published</option>
            <option value="failed">Failed</option>
            <option value="canceled">Canceled</option>
          </select>

          <Button variant="outline" onClick={fetchImports} size="sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {error && (
        <div className={`px-4 py-3 rounded-lg flex items-center gap-3 ${
          isAuthError 
            ? 'bg-amber-50 border border-amber-200 text-amber-800' 
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {isAuthError ? (
            <>
              <LogIn className="w-5 h-5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium">Session Expired</p>
                <p className="text-sm opacity-80">Your login session has expired. Please log in again to continue.</p>
              </div>
              <Button 
                size="sm" 
                onClick={() => window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname)}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Log In
              </Button>
            </>
          ) : (
            <>
              <XCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
              <Button variant="ghost" size="sm" onClick={fetchImports}>
                Retry
              </Button>
            </>
          )}
        </div>
      )}

      {/* Imports Table */}
      <div className="bg-white rounded-xl border border-ivory-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-ivory-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase">Import</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase">Vendor</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase">Stats</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase">Created</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ivory-100">
            {imports.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-ivory-500">
                  No imports found. Create your first import to get started.
                </td>
              </tr>
            ) : (
              imports.map((imp) => (
                <tr key={imp.id} className="hover:bg-ivory-50" data-testid={`import-row-${imp.id}`}>
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-mono text-sm text-ivory-800">{imp.id}</p>
                      <p className="text-xs text-ivory-500">{imp.file_name || 'No file'}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-ivory-800">{imp.vendor_name}</p>
                      <p className="text-xs text-ivory-500">{imp.vendor_code}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={imp.status} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-ivory-600">{imp.stats?.total_rows || 0} rows</span>
                      {imp.stats?.valid_rows > 0 && (
                        <span className="text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          {imp.stats.valid_rows}
                        </span>
                      )}
                      {imp.stats?.warning_rows > 0 && (
                        <span className="text-yellow-600 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {imp.stats.warning_rows}
                        </span>
                      )}
                      {imp.stats?.error_rows > 0 && (
                        <span className="text-red-600 flex items-center gap-1">
                          <XCircle className="w-3 h-3" />
                          {imp.stats.error_rows}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm text-ivory-600">{formatDate(imp.created_at)}</p>
                      <p className="text-xs text-ivory-400">{imp.created_by_email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewImport(imp.id)}
                      data-testid={`view-import-${imp.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-ivory-500">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCatalogImports;
